/* ═══════════════════════════════════════════════════════════
   timelineAnimation.js
   Toute la logique canvas réseau — indépendante de React.
   Appelée depuis NetworkCanvas (Parcours.jsx).
   ═══════════════════════════════════════════════════════════ */

const MOBILE_BP = 640; // doit correspondre au breakpoint CSS

/* ── Constantes de vitesse ── */
const SPEED = {
  packet:    { base: 0.7,  rand: 0.6  }, // paquet principal (descente)
  pingGo:    { base: 0.008, rand: 0.005 }, // ping aller  (connecteur → carte)
  pingAck:   { base: 0.010, rand: 0.005 }, // ping retour (carte → connecteur)
  pulse:     { expand: 1.4, fadeStep: 0.018 },
  dashStep:  0.5,
  spawnInterval: { min: 900, rand: 1500 },
};

/* ── Helpers ── */
export const easeOut = (t) => 1 - (1 - t) ** 2;

/* ══════════════════════════════════════════════
   RESIZE — recalcule dimensions + positions DOM
   ══════════════════════════════════════════════ */
export function resizeCanvas(canvas, timelineRef, state) {
  const tl = timelineRef.current;
  if (!tl) return;

  canvas.width  = tl.offsetWidth;
  canvas.height = tl.offsetHeight;

  const isMobile    = window.innerWidth <= MOBILE_BP;
  state.lineX       = isMobile ? 18 : canvas.width / 2;
  state.isMobile    = isMobile;

  const tlRect = tl.getBoundingClientRect();
  const items  = Array.from(tl.querySelectorAll('.timeline__item'));

  state.connectors = Array.from(tl.querySelectorAll('.timeline__connector')).map((el, i) => {
    const r  = el.getBoundingClientRect();
    const cx = r.left - tlRect.left + r.width  / 2;
    const cy = r.top  - tlRect.top  + r.height / 2;

    const side = isMobile ? 'right' : (i % 2 === 0 ? 'left' : 'right');

    /* Bord de la carte = destination exacte du ping */
    let cardEdgeX = cx + (side === 'right' ? 50 : -50);
    const card = items[i]?.querySelector('.timeline__content');
    if (card) {
      const cr  = card.getBoundingClientRect();
      cardEdgeX = side === 'right'
        ? cr.left  - tlRect.left   // bord gauche (carte à droite)
        : cr.right - tlRect.left;  // bord droit  (carte à gauche)
    }

    return { index: i, x: cx, y: cy, side, cardEdgeX };
  });
}

/* ══════════════════════════════════════════════
   FLUSH — vide tout ce qui est en vol
   ══════════════════════════════════════════════ */
export function flushAnimState(state) {
  state.packets = [];
  state.pings   = [];
  state.pulses  = [];
}

/* ══════════════════════════════════════════════
   PACKET — descend le long de la ligne centrale
   ══════════════════════════════════════════════ */
export function createPacket(state) {
  const vy = SPEED.packet.base + Math.random() * SPEED.packet.rand;
  return {
    x:         state.lineX,
    y:         -6,
    vy,
    size:      4 + Math.random() * 3,
    color:     Math.random() > 0.3
      ? `rgba(88,166,255,${(0.7 + Math.random() * 0.3).toFixed(2)})`
      : `rgba(46,160,67,${(0.7  + Math.random() * 0.3).toFixed(2)})`,
    triggered: new Set(),
    trail:     [],
    trailLen:  Math.floor(8 + vy * 5),
    dead:      false,
  };
}

export function updatePacket(packet, canvas, state, onConnectorHit) {
  packet.trail.push({ x: packet.x, y: packet.y });
  if (packet.trail.length > packet.trailLen) packet.trail.shift();
  packet.y += packet.vy;

  state.connectors.forEach((c) => {
    if (!packet.triggered.has(c.index) && Math.abs(packet.y - c.y) < packet.vy + 2) {
      packet.triggered.add(c.index);
      onConnectorHit(c);
    }
  });

  if (packet.y > canvas.height + 20) packet.dead = true;
}

export function drawPacket(ctx, packet) {
  packet.trail.forEach((p, i) => {
    const a = (i / packet.trail.length) * 0.5;
    ctx.beginPath();
    ctx.arc(p.x, p.y, packet.size * (i / packet.trail.length) * 0.8, 0, Math.PI * 2);
    ctx.fillStyle = packet.color.replace(/[\d.]+\)$/, `${a})`);
    ctx.fill();
  });
  ctx.beginPath();
  ctx.arc(packet.x, packet.y, packet.size, 0, Math.PI * 2);
  ctx.fillStyle   = packet.color;
  ctx.shadowColor = packet.color;
  ctx.shadowBlur  = 10;
  ctx.fill();
  ctx.shadowBlur  = 0;
}

/* ══════════════════════════════════════════════
   PING — trait horizontal connecteur ↔ carte
   ══════════════════════════════════════════════ */
export function spawnPing(state, c) {
  state.pings.push({
    startX: c.x, x: c.x, y: c.y,
    tx:     c.cardEdgeX,
    progress: 0,
    speed:    SPEED.pingGo.base + Math.random() * SPEED.pingGo.rand,
    color:    `rgba(88,166,255,${(0.6 + Math.random() * 0.4).toFixed(2)})`,
    size:     3 + Math.random() * 2,
    trail:    [],
    done:     false,
    ack:      false,
  });
}

export function updatePings(state) {
  state.pings.forEach((p) => {
    if (p.done) return;
    p.trail.push({ x: p.x, y: p.y });
    if (p.trail.length > 10) p.trail.shift();

    p.progress = Math.min(p.progress + p.speed, 1);
    p.x = p.startX + (p.tx - p.startX) * easeOut(p.progress);

    if (p.progress >= 1) {
      p.done = true;
      /* ACK retour vers la ligne */
      state.pings.push({
        startX: p.tx, x: p.tx, y: p.y,
        tx:       p.startX,
        progress: 0,
        speed:    SPEED.pingAck.base + Math.random() * SPEED.pingAck.rand,
        color:    'rgba(46,160,67,0.7)',
        size:     2.5,
        trail:    [],
        done:     false,
        ack:      true,
      });
    }
  });

  /* nettoyage pings aller terminés + acks terminés */
  for (let i = state.pings.length - 1; i >= 0; i--) {
    const p = state.pings[i];
    if (p.done && (p.ack || p.progress >= 1)) state.pings.splice(i, 1);
  }
}

export function drawPings(ctx, state) {
  state.pings.forEach((p) => {
    p.trail.forEach((pt, i) => {
      const a = (i / p.trail.length) * 0.35;
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, p.size * (i / p.trail.length) * 0.7, 0, Math.PI * 2);
      ctx.fillStyle = p.color.replace(/[\d.]+\)$/, `${a})`);
      ctx.fill();
    });
    if (!p.done) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fillStyle   = p.color;
      ctx.shadowColor = p.color;
      ctx.shadowBlur  = 8;
      ctx.fill();
      ctx.shadowBlur  = 0;
    }
  });
}

/* ══════════════════════════════════════════════
   PULSE — onde circulaire au connecteur
   ══════════════════════════════════════════════ */
export function spawnPulse(state, c) {
  state.pulses.push({ x: c.x, y: c.y, r: 20, maxR: 55, alpha: 0.8 });
}

export function updatePulses(state) {
  state.pulses.forEach((p) => {
    p.r     += SPEED.pulse.expand;
    p.alpha -= SPEED.pulse.fadeStep;
    if (p.r > p.maxR) p.alpha = 0;
  });
  for (let i = state.pulses.length - 1; i >= 0; i--) {
    if (state.pulses[i].alpha <= 0) state.pulses.splice(i, 1);
  }
}

export function drawPulses(ctx, state) {
  state.pulses.forEach((p) => {
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(88,166,255,${p.alpha.toFixed(3)})`;
    ctx.lineWidth   = 1.5;
    ctx.stroke();
  });
}

/* ══════════════════════════════════════════════
   LIGNE — tirets animés sur l'axe central
   ══════════════════════════════════════════════ */
export function drawLine(ctx, canvas, state) {
  ctx.setLineDash([8, 6]);
  ctx.lineDashOffset = -state.dashOffset;
  ctx.beginPath();
  ctx.moveTo(state.lineX, 0);
  ctx.lineTo(state.lineX, canvas.height);
  ctx.strokeStyle = 'rgba(88,166,255,0.3)';
  ctx.lineWidth   = 1.5;
  ctx.stroke();
  ctx.setLineDash([]);
  state.dashOffset = (state.dashOffset + SPEED.dashStep) % 14;
}

/* ══════════════════════════════════════════════
   SPAWN — intervalle aléatoire entre paquets
   ══════════════════════════════════════════════ */
export function maybeSpawn(now, state) {
  const interval = SPEED.spawnInterval.min + Math.random() * SPEED.spawnInterval.rand;
  if (now - state.lastSpawn > interval) {
    state.packets.push(createPacket(state));
    state.lastSpawn = now;
  }
}

/* ══════════════════════════════════════════════
   INITIAL STATE — factory pour stateRef.current
   ══════════════════════════════════════════════ */
export function createAnimState() {
  return {
    packets:    [],
    pings:      [],
    pulses:     [],
    connectors: [],
    animId:     null,
    lineX:      0,
    isMobile:   false,
    dashOffset: 0,
    lastSpawn:  0,
  };
}